import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import api, { setDemoMode } from '../lib/api';
import { setOAuthRoutingState, getOAuthRoutingState as readOAuthRoutingState } from '../lib/productRouting';

const AuthContext = createContext({});

// Demo account constants
const DEMO_USER_ID = "demo-geni-account-001";

// Helper to map network timeouts, Cloudflare 522 or 5xx connection errors on Supabase Auth
const getFriendlyAuthError = (error) => {
  if (!error) return null;
  const message = (error.message || '').toLowerCase();
  const code = error.code || '';
  const status = error.status;

  if (message.includes('failed to fetch') || 
      message.includes('timeout') || 
      message.includes('timed out') || 
      message.includes('522') || 
      message.includes('504') || 
      message.includes('network') || 
      message.includes('connection') ||
      status === 522 ||
      status === 504 ||
      code.includes('network_error')) {
    return new Error('The authentication service is temporarily unavailable (Error 522/Timeout). Your credentials were not verified. Please try again in a few moments.');
  }
  return null;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Demo mode state
  const [isDemo, setIsDemo] = useState(false);
  const [demoUser, setDemoUser] = useState(null);
  const [originalUser, setOriginalUser] = useState(null);
  const [originalSession, setOriginalSession] = useState(null);

  useEffect(() => {
    // Check active session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/confirm`,
        }
      });
      
      if (error) {
        const friendlyErr = getFriendlyAuthError(error);
        if (friendlyErr) throw friendlyErr;

        // Handle rate limit errors (including body stream already read which often indicates 429)
        if (error.status === 429 || 
            error.message?.includes('rate limit') ||
            error.message?.includes('body stream already read')) {
          throw new Error('Too many attempts. Please wait a few minutes and try again.');
        }
        // Handle already registered
        if (error.message?.includes('already registered') || error.code === 'user_already_exists') {
          throw new Error('An account with this email already exists.');
        }
        throw new Error(error.message || 'Failed to create account');
      }
      
      // SUCCESS: Return structured response with confirmation status
      // When email confirmation is enabled: user exists but session is null
      // When email confirmation is disabled: both user and session exist
      return {
        user: data.user,
        session: data.session,
        confirmationRequired: data.user && !data.session,
      };
    } catch (err) {
      // Re-throw errors - they're already formatted
      throw err;
    }
  };

  // Resend confirmation email
  const resendConfirmation = async (email) => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/login`,
        }
      });
      
      if (error) {
        if (error.message?.includes('rate limit') || error.status === 429) {
          throw new Error('Too many attempts. Please wait a moment and try again.');
        }
        throw new Error(error.message || 'Failed to resend confirmation email');
      }
      
      return { success: true };
    } catch (err) {
      if (err.message?.includes('body stream already read')) {
        throw new Error('Too many requests. Please wait a moment and try again.');
      }
      throw err;
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        const friendlyErr = getFriendlyAuthError(error);
        if (friendlyErr) throw friendlyErr;

        // Handle specific Supabase error codes
        if (error.message?.includes('Invalid login credentials')) {
          throw new Error('Invalid email or password');
        }
        if (error.message?.includes('rate limit') || error.status === 429) {
          throw new Error('Too many attempts. Please wait a moment and try again.');
        }
        // Pass through email not confirmed error so Login page can show proper message
        if (error.message?.includes('Email not confirmed')) {
          throw new Error('Email not confirmed. Please check your inbox and confirm your email.');
        }
        throw new Error(error.message || 'Failed to sign in');
      }
      
      // Check for pending product updates preference from signup
      const pendingPreference = localStorage.getItem('pending_product_updates_preference');
      if (pendingPreference === 'true' && data?.session) {
        try {
          await api.post('/user/product-updates-preference', { wants_product_updates: true });
          localStorage.removeItem('pending_product_updates_preference');
        } catch (prefErr) {
          console.error('Error saving pending product updates preference:', prefErr);
        }
      }
      
      return data;
    } catch (err) {
      // Handle body stream already read error
      if (err.message?.includes('body stream already read')) {
        throw new Error('Too many requests. Please wait a moment and try again.');
      }
      throw err;
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Sign in with Google OAuth
  const signInWithGoogle = async (redirectTo = null, options = {}) => {
    try {
      setOAuthRoutingState({
        redirectTo: redirectTo || '/dashboard',
        allowAcquisition: Boolean(options?.allowAcquisition),
      });
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/confirm`,
        }
      });
      
      if (error) {
        throw new Error(error.message || 'Failed to initiate Google sign-in');
      }
      
      return data;
    } catch (err) {
      console.error('Google sign-in error:', err);
      throw err;
    }
  };

  // Get stored OAuth routing payload (called after OAuth completes)
  const getOAuthRoutingState = useCallback(() => {
    return readOAuthRoutingState();
  }, []);

  // Get access token for API calls
  const getAccessToken = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  }, []);

  // Helper to make authenticated API calls
  const authFetch = useCallback(async (url, options = {}) => {
    const token = await getAccessToken();
    
    if (!token) {
      throw new Error('Not authenticated');
    }

    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
    };

    return fetch(url, { ...options, headers });
  }, [getAccessToken]);

  // Switch to demo account
  const enterDemoMode = useCallback(async () => {
    try {
      // Enable demo mode in API
      setDemoMode(true);
      
      // Fetch demo account info
      const response = await api.get('/demo/account');
      const demoData = response.data;
      
      // Store original user for switching back
      setOriginalUser(user);
      setOriginalSession(session);
      
      // Create a mock demo user object
      const mockDemoUser = {
        id: demoData.user_id,
        email: demoData.email,
        user_metadata: {
          username: demoData.username,
        },
        is_demo: true,
      };
      
      setDemoUser(demoData);
      setUser(mockDemoUser);
      setIsDemo(true);
      
      return { success: true, demoData };
    } catch (error) {
      console.error('Failed to enter demo mode:', error);
      setDemoMode(false); // Reset on failure
      
      // Provide user-friendly error messages
      let errorMessage = 'Failed to load demo account';
      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = 'Demo account is being set up. Please try again in a moment.';
        } else if (error.response.status === 500) {
          errorMessage = 'Server error loading demo. Please try again later.';
        } else {
          errorMessage = error.response.data?.detail || errorMessage;
        }
      } else if (error.code === 'NO_AUTH_SESSION') {
        errorMessage = 'Please log in first to try the demo account.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { success: false, error: errorMessage };
    }
  }, [user, session]);

  // Exit demo mode and return to original user
  const exitDemoMode = useCallback(() => {
    setDemoMode(false); // Disable demo mode in API
    
    if (originalUser) {
      setUser(originalUser);
      setSession(originalSession);
    }
    setIsDemo(false);
    setDemoUser(null);
    setOriginalUser(null);
    setOriginalSession(null);
  }, [originalUser, originalSession]);

  // Get the effective user ID (demo or real)
  const getEffectiveUserId = useCallback(() => {
    if (isDemo) {
      return DEMO_USER_ID;
    }
    return user?.id;
  }, [isDemo, user]);

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resendConfirmation,
    getAccessToken,
    authFetch,
    getOAuthRoutingState,
    // Demo mode
    isDemo,
    demoUser,
    enterDemoMode,
    exitDemoMode,
    getEffectiveUserId,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
